import { ResizeObserver as Polyfill } from "@juggle/resize-observer";
import {
  AlertContext,
  useAlertContext,
} from "components/alert-bar/useAlertContext";
import CookieModal from "components/cookie-modal";
import Error from "components/error";
import Footer from "components/footer";
import Loading from "components/loading/page-loading";
import Nav from "components/nav";
import Page from "components/page";
import ScrollMemory from "components/scroll-memory";
import TextWaveSandbox from "components/text-wave-sandbox";
import WaveUnderline from "components/wave-underline";
import clipWave from "images/image_clip_wave.svg";
import "lazysizes";
import TagManager from "react-gtm-module";
import SVG from "react-inlinesvg";
import { Route, Routes } from "react-router-dom";
import { parseUrlForGraphQL } from "../../helpers";
import useLocationChange from "../../hooks/useLocationChange";
import usePageOptions from "../../hooks/usePageOptions";
import { PAGE_TYPE, PAGE_TYPEHANDLE } from "../../pageTypes";

const GTM_ID = "GTM-PCS7NHG";

if (typeof window !== "undefined") {
  window.ResizeObserver = window.ResizeObserver || Polyfill;
}

if (typeof document !== "undefined") {
  TagManager.initialize({ gtmId: GTM_ID });
}

const App = () => {
  // Notify GTM of SPA navigation so GA4 (configured in GTM) can fire a page_view.
  // useLocationChange deliberately skips the initial render: the page load itself
  // is already counted by the GTM container, so firing here too would double
  // every pageview.
  useLocationChange((location) => {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "virtualPageView",
        pagePath: location.pathname + location.search,
      });
    }
    document.activeElement.blur();
  });

  const url = typeof document !== "undefined" ? document.location.pathname : "";
  const { data } = usePageOptions(parseUrlForGraphQL(url));

  const isPrivacyDataCenter =
    data?.entry?.typeHandle === PAGE_TYPEHANDLE.PRIVACY_CENTER;
  const hasBackground = data?.entry?.ismainnavbackgrounded ?? false;
  const isSmoothScrollDisabled = data?.entry?.isSmoothScrollDisabled ?? false;
  const showFooter = data?.entry?.showFooter ?? true;
  const showCookieModal = data?.entry?.showCookieModal ?? true;

  return (
    <>
      <ScrollMemory />
      {!isPrivacyDataCenter ? (
        <MainRoutes
          isSmoothScrollDisabled={isSmoothScrollDisabled}
          hasBackground={hasBackground}
          showFooter={showFooter}
          showCookieModal={showCookieModal}
        />
      ) : (
        <PrivacyCenterRoutes />
      )}
    </>
  );
};

const PrivacyCenterRoutes = () => {
  return (
    <div className={`AppPrivacyCenter`}>
      <SVG
        src={clipWave}
        className={"AppWaveSvg"}
        style={{ position: "absolute" }}
      />
      <Nav navType={PAGE_TYPE.PRIVACY_CENTER} />
      <Routes>
        <Route path={"/error"} element={<Error />} />
        <Route path={"/loading"} element={<Loading />} />
        <Route path={"/clubs-privacy-center"} element={<Page />} />
      </Routes>
    </div>
  );
};

// Named MainRoutes because `Routes` is now react-router's own component.
const MainRoutes = ({
  hasBackground = false,
  showFooter = true,
  showCookieModal = true,
  isSmoothScrollDisabled = false,
}) => {
  const alertContext = useAlertContext();
  const isSmoothScrollDisabledClassName = !!isSmoothScrollDisabled
    ? "SmoothScrollDisabled"
    : "";
  return (
    <AlertContext.Provider value={alertContext}>
      <div className={`App ${isSmoothScrollDisabledClassName}`}>
        <SVG
          src={clipWave}
          className={"AppWaveSvg"}
          style={{ position: "absolute" }}
        />
        <Nav navType={PAGE_TYPE.PAGE} hasBackground={hasBackground} />
        {/* v6 dropped <Switch>: <Routes> ranks its children by specificity
            instead of taking the first that matches, so the static paths below
            still win over /:slug? regardless of the order they appear in. */}
        <Routes>
          <Route path={"/error"} element={<Error />} />
          <Route path={"/loading"} element={<Loading />} />
          <Route path={"/wave-maker"} element={<TextWaveSandbox />} />
          <Route
            path={"/text-wave"}
            element={
              <WaveUnderline className="HeadingText-heading">
                This heading here
              </WaveUnderline>
            }
          />
          <Route path={"/news/:slug"} element={<Page />} />
          <Route path={"/:parent/:slug"} element={<Page />} />
          <Route path={"/:slug?"} element={<Page />} />
          {/* v5 matched by prefix, so /a/b/c fell into /:parent/:slug; v6 matches
              whole paths and left deeper URLs with an empty page. Page asks Craft
              about the full path and shows NotFound when there is no entry. */}
          <Route path={"*"} element={<Page />} />
        </Routes>
        {showFooter && <Footer />}
        {showCookieModal && <CookieModal />}
      </div>
    </AlertContext.Provider>
  );
};

export default App;
